import axios from 'axios';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Shield, Save, Info, FileText, Building, Wrench, CheckCircle, ChevronDown, ChevronUp, Bold, Italic, List, ListOrdered, Upload, Trash2, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import '../components/crm/EditorRiche.css';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const DOC_CONFIG = [
  {
    code: 'decharge_legale',
    label: 'Décharge Légale',
    icon: Shield,
    color: 'text-purple-600',
    bg: 'bg-purple-50 border-purple-200',
    badgeColor: 'bg-purple-100 text-purple-800',
    desc: 'Reliée à la section "Décharge" dans le dossier client — envoi séparé via la fiche du dossier',
    dept: null,
    isDecharge: true,  // section séparée
  },
  {
    code: 'garantie_prqc',
    label: 'Garantie des travaux',
    icon: CheckCircle,
    color: 'text-blue-600',
    bg: 'bg-blue-50 border-blue-200',
    badgeColor: 'bg-blue-100 text-blue-800',
    desc: 'Annexe ajoutée automatiquement aux soumissions PRQC (Peinture)',
    dept: 'PRQC',
  },
  {
    code: 'modalites_peinture',
    label: 'Modalités & conditions — Peinture',
    icon: FileText,
    color: 'text-green-600',
    bg: 'bg-green-50 border-green-200',
    badgeColor: 'bg-green-100 text-green-800',
    desc: 'Annexe ajoutée automatiquement aux soumissions PRQC (Peinture)',
    dept: 'PRQC',
  },
  {
    code: 'modalites_location',
    label: 'Modalités & conditions — Location',
    icon: Wrench,
    color: 'text-orange-600',
    bg: 'bg-orange-50 border-orange-200',
    badgeColor: 'bg-orange-100 text-orange-800',
    desc: 'Annexe ajoutée automatiquement aux soumissions LOCATION',
    dept: 'LOCATION',
  },
];

const VARIABLES = ['{client_nom}', '{adresse_travaux}', '{ville_travaux}', '{montant}', '{date}'];

function DocEditor({ config, value, onChange, saving, onSave, onUploadPdf, onDeletePdf, uploadingPdf }) {
  const [open, setOpen] = useState(false);
  const Icon = config.icon;
  const hasPdf = !!value?.pdf_url;

  const editor = useEditor({
    extensions: [StarterKit],
    content: value?.contenu || '',
    onUpdate: ({ editor }) => {
      onChange({ ...value, contenu: editor.getHTML() });
    },
  });

  useEffect(() => {
    if (editor && value?.contenu !== undefined && value.contenu !== editor.getHTML()) {
      editor.commands.setContent(value.contenu || '');
    }
  }, [value?.contenu, editor]);

  const toolbarBtn = (active) => ({
    backgroundColor: active ? '#1f2937' : '#f3f4f6',
    color: active ? 'white' : '#374151',
    border: '1px solid #d1d5db',
  });

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Seuls les fichiers PDF sont acceptés');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10 Mo)');
      return;
    }
    onUploadPdf(config.code, file);
    e.target.value = '';
  };

  return (
    <Card className={`border ${open ? 'border-slate-300 shadow-sm' : 'border-slate-200'} transition-all`}>
      <button
        type="button"
        className="w-full text-left"
        onClick={() => setOpen(o => !o)}
        data-testid={`doc-section-${config.code}`}
      >
        <CardHeader className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${config.bg}`}>
                <Icon className={`w-4 h-4 ${config.color}`} />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-800 text-sm">{config.label}</p>
                <p className="text-xs text-slate-500">{config.desc}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {hasPdf && <Badge className="text-xs bg-emerald-100 text-emerald-700">PDF</Badge>}
              {config.dept && (
                <Badge className={`text-xs ${config.badgeColor}`}>{config.dept}</Badge>
              )}
              {!config.dept && (
                <Badge className="text-xs bg-slate-100 text-slate-600">Universel</Badge>
              )}
              {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </div>
          </div>
        </CardHeader>
      </button>

      {open && (
        <CardContent className="pt-0 pb-4 space-y-3">
          <div>
            <Label className="text-xs">Nom du document</Label>
            <Input
              value={value?.nom || ''}
              onChange={e => onChange({ ...value, nom: e.target.value })}
              placeholder={config.label}
              data-testid={`input-nom-${config.code}`}
            />
          </div>

          {/* Section PDF — pour les documents non-décharge */}
          {!config.isDecharge && (
            <div className="border rounded-lg p-3 bg-slate-50 space-y-2">
              <Label className="text-xs font-semibold flex items-center gap-1.5">
                <FileDown className="w-3.5 h-3.5" /> Fichier PDF joint
              </Label>
              {hasPdf ? (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 flex-1 bg-white border rounded-md px-3 py-2">
                    <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span className="text-sm truncate">{value.pdf_filename || 'document.pdf'}</span>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    onClick={() => onDeletePdf(config.code)}
                    disabled={uploadingPdf}
                    data-testid={`delete-pdf-${config.code}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Retirer
                  </Button>
                </div>
              ) : (
                <div>
                  <label className="cursor-pointer">
                    <input type="file" accept=".pdf" onChange={handleFileSelect} className="hidden" data-testid={`upload-pdf-input-${config.code}`} />
                    <div className="flex items-center gap-2 border-2 border-dashed rounded-md px-4 py-3 hover:bg-white transition-colors text-center justify-center">
                      <Upload className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-500">
                        {uploadingPdf ? 'Upload en cours...' : 'Cliquer pour uploader un PDF (max 10 Mo)'}
                      </span>
                    </div>
                  </label>
                </div>
              )}
              {hasPdf && (
                <p className="text-xs text-emerald-600">Ce PDF sera joint automatiquement aux soumissions envoyees par courriel.</p>
              )}
              {!hasPdf && (
                <p className="text-xs text-slate-400">Si un PDF est uploade, il remplacera le texte ci-dessous comme piece jointe.</p>
              )}
            </div>
          )}

          <div>
            <Label className="text-xs">Contenu {hasPdf && !config.isDecharge ? '(utilise comme fallback si pas de PDF)' : ''}</Label>
            {editor && (
              <div className="editor-container border rounded-lg overflow-hidden" data-testid={`editor-${config.code}`}>
                <div className="flex items-center gap-1 p-2 bg-gray-50 border-b">
                  <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0"
                    style={toolbarBtn(editor.isActive('bold'))}
                    onClick={() => editor.chain().focus().toggleBold().run()}>
                    <Bold className="w-3.5 h-3.5" />
                  </Button>
                  <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0"
                    style={toolbarBtn(editor.isActive('italic'))}
                    onClick={() => editor.chain().focus().toggleItalic().run()}>
                    <Italic className="w-3.5 h-3.5" />
                  </Button>
                  <div className="w-px h-5 bg-gray-300 mx-1" />
                  <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0"
                    style={toolbarBtn(editor.isActive('bulletList'))}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}>
                    <List className="w-3.5 h-3.5" />
                  </Button>
                  <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0"
                    style={toolbarBtn(editor.isActive('orderedList'))}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}>
                    <ListOrdered className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <EditorContent editor={editor} className="tiptap-editor prose prose-sm max-w-none p-3 min-h-[200px]" />
              </div>
            )}
          </div>
          <div className="flex justify-between items-center pt-1">
            <p className="text-xs text-slate-400">
              {value?.updated_at && `Modifie: ${new Date(value.updated_at).toLocaleDateString('fr-CA')}`}
            </p>
            <Button
              size="sm"
              onClick={onSave}
              disabled={saving}
              data-testid={`save-doc-${config.code}`}
            >
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export function ConfigurationLegal() {
  const [documents, setDocuments] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  useEffect(() => { fetchDocuments(); }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/legal/documents-departement`);
      if (res.status === 200) {
        const d = res.data;
        setDocuments(d.documents || {});
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const saveDocument = async (code) => {
    setSaving(code);
    try {
      const res = await axios.put(`${API_URL}/api/legal/documents-departement`, { documents: { [code]: documents[code] } });
      if (res.status !== 200) throw new Error();
      toast.success('Document sauvegarde !');
      fetchDocuments();
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(null);
    }
  };

  const handleUploadPdf = async (code, file) => {
    setUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/legal/upload-pdf/${code}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.status !== 200) {
        const err = res.data || {};  // 🆕 Phase E2 — fix copy-paste axios (data déjà parsé)
        throw new Error(err.detail || 'Erreur upload');
      }
      const result = res.data;
      toast.success(`PDF uploade: ${file.name}`);
      setDocuments(d => ({
        ...d,
        [code]: { ...d[code], pdf_url: result.pdf_url, pdf_filename: result.pdf_filename },
      }));
    } catch (e) {
      toast.error(e.message || 'Erreur upload PDF');
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleDeletePdf = async (code) => {
    try {
      const res = await axios.delete(`${API_URL}/api/legal/delete-pdf/${code}`);
      if (res.status !== 200) throw new Error();
      toast.success('PDF retire');
      setDocuments(d => {
        const updated = { ...d[code] };
        delete updated.pdf_url;
        delete updated.pdf_filename;
        return { ...d, [code]: updated };
      });
    } catch {
      toast.error('Erreur suppression PDF');
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Chargement...</div>;

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Shield className="w-7 h-7 text-purple-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Légal & Décharges</h1>
          <p className="text-sm text-gray-500">Gérez vos documents légaux — ajoutés automatiquement aux soumissions selon le département</p>
        </div>
      </div>

      {/* Règle auto-assignation */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-2">Fonctionnement des documents :</p>
              <div className="space-y-1 text-xs">
                <div className="flex items-start gap-2">
                  <Badge className="bg-purple-100 text-purple-700 shrink-0 mt-0.5">Décharge</Badge>
                  <span>Reliée à la section <strong>Décharge</strong> du dossier client. Envoyée séparément (lien unique). <strong>Non incluse dans les documents joints de la soumission.</strong></span>
                </div>
                <div className="flex items-start gap-2 pt-1 border-t border-blue-200">
                  <span className="font-semibold shrink-0">Documents Joints (soumission) :</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-blue-100 text-blue-700">PRQC</Badge>
                  <span>Garantie + Modalités Peinture ajoutées automatiquement</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-orange-100 text-orange-700">LOCATION</Badge>
                  <span>Modalités Location ajoutées automatiquement</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-slate-100 text-slate-600">LMDS</Badge>
                  <span>Aucun document joint</span>
                </div>
              </div>
              <p className="mt-2 text-xs font-medium">Variables disponibles : {VARIABLES.map(v => (
                <Badge key={v} className="bg-blue-100 text-blue-800 font-mono text-xs mx-0.5">{v}</Badge>
              ))}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section décharge légale — séparée */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
          <Shield className="w-4 h-4 text-purple-600" /> Décharge Légale
          <Badge className="bg-purple-100 text-purple-700 text-xs">Dossier client uniquement</Badge>
        </h2>
        {DOC_CONFIG.filter(c => c.isDecharge).map(cfg => (
          <DocEditor
            key={cfg.code}
            config={cfg}
            value={documents[cfg.code] || {}}
            onChange={val => setDocuments(d => ({ ...d, [cfg.code]: val }))}
            saving={saving === cfg.code}
            onSave={() => saveDocument(cfg.code)}
            onUploadPdf={handleUploadPdf}
            onDeletePdf={handleDeletePdf}
            uploadingPdf={uploadingPdf}
          />
        ))}
      </div>

      {/* Documents annexes soumissions */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
          <FileText className="w-4 h-4 text-blue-600" /> Annexes — Documents Joints (Soumissions)
        </h2>
        <div className="space-y-3">
          {DOC_CONFIG.filter(c => !c.isDecharge).map(cfg => (
            <DocEditor
              key={cfg.code}
              config={cfg}
              value={documents[cfg.code] || {}}
              onChange={val => setDocuments(d => ({ ...d, [cfg.code]: val }))}
              saving={saving === cfg.code}
              onSave={() => saveDocument(cfg.code)}
              onUploadPdf={handleUploadPdf}
              onDeletePdf={handleDeletePdf}
              uploadingPdf={uploadingPdf}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
